// ConsentGate.tsx — CBAM Phase 1 capture UI (CAP-1.2)
//
// The consent step shown BEFORE any audio leaves the device. It collects a lawful basis, the
// versioned US-vendor disclosure acknowledgement, the parties present, and the jurisdiction, then
// hands the caller a fully-formed `Consent` object whose disclosureVersion is pinned to
// CURRENT_DISCLOSURE_VERSION (must match supabase/functions/_shared/consent-guard.ts).
//
// It also exposes a "No consent — notes only" escape: when the capturer cannot affirm consent, the
// caller is signalled to take the manual_text path (no audio upload at all).
//
// Native-only feature surface, but this component is pure React (no native API calls), so it is safe
// to render anywhere; the website never mounts it.

import { useMemo, useState } from 'react';
import { Mic, ShieldAlert, FileText, Globe, Users, WifiOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useIsOnline } from '@/hooks/useIsOnline';
import { CURRENT_DISCLOSURE_VERSION, type Consent } from '@/lib/capture';

// Lawful-basis union — the four pinned values. Defined locally (derived from the Consent contract)
// rather than imported, so this file does not depend on Agent A also exporting a named basis type.
type ConsentBasis = Consent['basis'];

// The MANDATORY disclosure line. Shown verbatim; if this copy materially changes the
// CURRENT_DISCLOSURE_VERSION string must be bumped (in consent-guard.ts and re-exported via
// src/lib/capture) so existing consent rows become stale and are re-captured.
const MANDATORY_DISCLOSURE_LINE =
  'This audio is processed in the United States by Deepgram, and may be processed by additional US AI vendors.';

interface BasisOption {
  value: ConsentBasis;
  label: string;
  hint: string;
  /** When true, capturer must affirm that consent was obtained from the third parties present. */
  requiresAffirmation: boolean;
}

const BASIS_OPTIONS: BasisOption[] = [
  {
    value: 'all_parties_verbal',
    label: 'All parties — verbal consent',
    hint: 'Everyone present was told and agreed out loud before recording.',
    requiresAffirmation: true,
  },
  {
    value: 'written_on_file',
    label: 'Written consent on file',
    hint: 'A signed/written consent already covers this recording.',
    requiresAffirmation: true,
  },
  {
    value: 'public_seminar',
    label: 'Public seminar / open forum',
    hint: 'A public setting where recording is expected.',
    requiresAffirmation: false,
  },
  {
    value: 'internal_only',
    label: 'Internal only — colleagues',
    hint: 'Only Cape & Berkshire staff are present.',
    requiresAffirmation: false,
  },
];

export interface ConsentGateProps {
  /** Called with a complete Consent object when the capturer confirms. */
  onConfirm: (consent: Consent) => void;
  /** Called when the capturer chooses the notes-only escape (no audio upload). */
  onNotesOnly: () => void;
  /** Called when the capturer backs out entirely. */
  onCancel?: () => void;
  /** Optional pre-fill for jurisdiction (defaults to UK). */
  defaultJurisdiction?: string;
}

export function ConsentGate({
  onConfirm,
  onNotesOnly,
  onCancel,
  defaultJurisdiction = 'UK',
}: ConsentGateProps) {
  const online = useIsOnline();
  const [basis, setBasis] = useState<ConsentBasis | null>(null);
  const [affirmed, setAffirmed] = useState(false);
  const [partiesText, setPartiesText] = useState('');
  const [jurisdiction, setJurisdiction] = useState(defaultJurisdiction);
  const [emotionInferenceOk, setEmotionInferenceOk] = useState(false);

  const selectedOption = useMemo(
    () => BASIS_OPTIONS.find((o) => o.value === basis) ?? null,
    [basis],
  );

  const parties = useMemo(
    () =>
      partiesText
        .split(',')
        .map((p) => p.trim())
        .filter(Boolean),
    [partiesText],
  );

  // Confirm is allowed once: a basis is chosen, the required affirmation (if any) is ticked, and a
  // jurisdiction is present. Parties are encouraged but not strictly required for non-third-party bases.
  const requiresAffirmation = selectedOption?.requiresAffirmation ?? false;
  const canConfirm =
    !!basis &&
    jurisdiction.trim().length > 0 &&
    (!requiresAffirmation || affirmed);

  const handleConfirm = () => {
    if (!basis || !canConfirm) return;
    const consent: Consent = {
      basis,
      parties,
      jurisdiction: jurisdiction.trim(),
      disclosureVersion: CURRENT_DISCLOSURE_VERSION,
      // For third-party bases this affirmation is the human attestation that verbal/written consent
      // was actually obtained; it maps to verbalSegmentPresent in the pinned Consent contract.
      verbalSegmentPresent: requiresAffirmation ? affirmed : false,
      emotionInferenceOk,
    };
    onConfirm(consent);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 rounded-full bg-primary/10 p-2 text-primary">
          <Mic className="h-5 w-5" />
        </span>
        <div>
          <h3 className="font-heading text-base font-semibold">Before we record</h3>
          <p className="text-xs text-muted-foreground">
            Tell us the lawful basis for this capture.
          </p>
        </div>
      </div>

      {/* Mandatory US-vendor disclosure — shown verbatim, versioned. */}
      <div className="rounded-lg border border-amber-300/60 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-950/40 dark:text-amber-200">
        <div className="mb-1 flex items-center gap-1.5 font-medium">
          <ShieldAlert className="h-4 w-4" />
          US processing notice
        </div>
        <p>{MANDATORY_DISCLOSURE_LINE}</p>
        <p className="mt-1 text-[10px] opacity-70">
          Disclosure version {CURRENT_DISCLOSURE_VERSION}
        </p>
      </div>

      {/* Offline notice — recording still works; the audio is queued on-device and uploads on
          reconnect. Renders nothing when online so online behaviour is unchanged. */}
      {!online && (
        <div
          role="status"
          aria-live="polite"
          className="flex items-start gap-2 rounded-lg border border-amber-300/60 bg-amber-50 p-2.5 text-[11px] leading-snug text-amber-900 dark:border-amber-500/30 dark:bg-amber-950/40 dark:text-amber-200"
        >
          <WifiOff className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>
            You&apos;re offline. You can still record — the audio is saved on this device and uploads
            automatically once you&apos;re back online.
          </span>
        </div>
      )}

      {/* Lawful basis */}
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 text-sm font-medium">Lawful basis</legend>
        <RadioGroup
          value={basis ?? undefined}
          onValueChange={(v) => {
            setBasis(v as ConsentBasis);
            setAffirmed(false);
          }}
          className="gap-2"
        >
          {BASIS_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              htmlFor={`basis-${opt.value}`}
              className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3 has-[:checked]:border-primary has-[:checked]:bg-primary/5"
            >
              <RadioGroupItem id={`basis-${opt.value}`} value={opt.value} className="mt-0.5" />
              <span className="leading-tight">
                <span className="block text-sm font-medium">{opt.label}</span>
                <span className="block text-xs text-muted-foreground">{opt.hint}</span>
              </span>
            </label>
          ))}
        </RadioGroup>
      </fieldset>

      {/* Explicit affirmation for third-party bases (maps to verbalSegmentPresent). */}
      {requiresAffirmation && (
        <label
          htmlFor="consent-affirm"
          className="flex cursor-pointer items-start gap-3 rounded-lg border border-primary/40 bg-primary/5 p-3"
        >
          <Checkbox
            id="consent-affirm"
            checked={affirmed}
            onCheckedChange={(c) => setAffirmed(c === true)}
            className="mt-0.5"
          />
          <span className="text-xs leading-snug">
            I confirm consent was obtained from all parties for this recording, and that they were
            told about the US processing described above.
          </span>
        </label>
      )}

      {/* Parties */}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="consent-parties" className="flex items-center gap-1.5 text-sm">
          <Users className="h-3.5 w-3.5" /> Parties present
        </Label>
        <Input
          id="consent-parties"
          inputMode="text"
          autoCapitalize="words"
          placeholder="e.g. Jane Doe, Acme Corp (comma separated)"
          value={partiesText}
          onChange={(e) => setPartiesText(e.target.value)}
        />
      </div>

      {/* Jurisdiction */}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="consent-jurisdiction" className="flex items-center gap-1.5 text-sm">
          <Globe className="h-3.5 w-3.5" /> Jurisdiction
        </Label>
        <Input
          id="consent-jurisdiction"
          value={jurisdiction}
          onChange={(e) => setJurisdiction(e.target.value)}
          placeholder="UK"
        />
      </div>

      {/* Emotion inference opt-in (off by default). */}
      <label
        htmlFor="consent-emotion"
        className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3"
      >
        <Checkbox
          id="consent-emotion"
          checked={emotionInferenceOk}
          onCheckedChange={(c) => setEmotionInferenceOk(c === true)}
          className="mt-0.5"
        />
        <span className="text-xs leading-snug text-muted-foreground">
          Allow tone / emotion inference on this audio (optional).
        </span>
      </label>

      <div className="mt-1 flex flex-col gap-2">
        <Button type="button" onClick={handleConfirm} disabled={!canConfirm} className="w-full">
          Confirm &amp; continue
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onNotesOnly}
          className="w-full gap-2"
        >
          <FileText className="h-4 w-4" /> No consent — notes only
        </Button>
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel} className="w-full">
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
}
